import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Badge,
  Alert,
  Loader,
  Center,
} from "@mantine/core";
import { MonthPickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import {
  IconDownload,
  IconFileTypePdf,
  IconInfoCircle,
} from "@tabler/icons-react";
import { PageHeader } from "../../components/common/PageHeader";
import { StatCard } from "../../components/common/StatCard";
import { DataTable, type Column } from "../../components/common/DataTable";
import { QueryError } from "../../components/common/QueryError";
import { latraReportApi } from "../../api/settlements";
import { errorMessage } from "../../lib/axios";
import { formatMoney } from "../../lib/format";
import { humanize } from "../../lib/labels";
import type { LatraMonthlyReport } from "../../types/domain";

type BreakdownRow = LatraMonthlyReport["breakdown"][number];

/** Default to the previous calendar month — the one that is actually payable. */
function previousMonthDate(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() - 1, 1);
}

function toPeriod(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function LatraMonthlyReportPage() {
  const [month, setMonth] = useState<Date | null>(previousMonthDate());
  const [downloading, setDownloading] = useState<"csv" | "pdf" | null>(null);

  const period = month ? toPeriod(month) : toPeriod(previousMonthDate());

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["latra-monthly", period],
    queryFn: () => latraReportApi.monthly(period),
  });

  const download = async (format: "csv" | "pdf") => {
    setDownloading(format);
    try {
      await latraReportApi.download(period, format);
    } catch (e) {
      notifications.show({ color: "red", message: errorMessage(e) });
    } finally {
      setDownloading(null);
    }
  };

  const columns: Column<BreakdownRow>[] = [
    { key: "day", header: "Date (EAT)", render: (r) => r.day },
    {
      key: "paymentMode",
      header: "Payment method",
      render: (r) => humanize(r.paymentMode),
    },
    {
      key: "tripCount",
      header: "Trips",
      align: "right",
      render: (r) => r.tripCount,
    },
    {
      key: "latraLevy",
      header: "LATRA levy collected",
      align: "right",
      render: (r) => formatMoney(r.latraLevy ?? 0),
    },
  ];

  return (
    <Stack>
      <PageHeader
        title="LATRA Monthly Report"
        subtitle="LATRA levy collected from riders, payable to LATRA"
        actions={
          <Group gap="xs">
            <MonthPickerInput
              value={month}
              onChange={(val) =>
                setMonth(typeof val === "string" ? new Date(val) : val)
              }
              placeholder="Pick month"
              maxDate={new Date()}
              w={180}
            />
            <Button
              variant="default"
              leftSection={<IconDownload size={16} />}
              loading={downloading === "csv"}
              onClick={() => download("csv")}
            >
              CSV
            </Button>
            <Button
              variant="default"
              leftSection={<IconFileTypePdf size={16} />}
              loading={downloading === "pdf"}
              onClick={() => download("pdf")}
            >
              PDF
            </Button>
          </Group>
        }
      />

      <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />}>
        The levy is recognised on the date Smart Safiri{" "}
        <strong>collected</strong> the trip's money, like the booking fee, not
        the trip date. A cash trip completed in one month but settled the next
        counts toward the later month. Levy that is never collected is excluded.
      </Alert>

      {isLoading ? (
        <Center h={200}>
          <Loader />
        </Center>
      ) : error ? (
        <QueryError error={error} onRetry={() => refetch()} />
      ) : data ? (
        <>
          {data.isProvisional && (
            <Alert variant="light" color="yellow">
              This month is still open — figures are provisional and will change
              as more trip money is collected.
            </Alert>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            <StatCard
              label="LATRA levy rate"
              value={`${data.latraLevyPercentage ?? 0}%`}
              sub="Of each trip's actual fare"
            />
            <StatCard
              label="Levy collected"
              value={formatMoney(data.collectedLatraLevy ?? 0)}
              sub="Paid by riders"
            />
            <StatCard
              label="Amount payable"
              value={formatMoney(data.latraAmount)}
            />
            <StatCard
              label="Trips settled"
              value={String(data.tripCount)}
              sub={`${data.driverCount} drivers`}
            />
          </SimpleGrid>

          <Card withBorder radius="md" p="xs">
            <Group justify="space-between" p="xs">
              <Badge
                color={data.status === "synced" ? "green" : "gray"}
                variant="light"
              >
                {humanize(String(data.status))}
              </Badge>
              {data.referenceNumber && (
                <Badge variant="outline">Ref: {data.referenceNumber}</Badge>
              )}
            </Group>
            <DataTable<BreakdownRow>
              columns={columns}
              rows={data.breakdown}
              rowKey={(r) => `${r.day}-${r.paymentMode}`}
              emptyLabel="No levy collected in this period"
            />
          </Card>
        </>
      ) : null}
    </Stack>
  );
}
